<script lang="ts">
    import {
        VpCalendarIcon,
        VpCheckIcon,
        VpReturnIcon,
    } from '@decathlon-experience/icons/svelte';
    import { parseDate } from '@internationalized/date';
    import { createDateRangePicker, createTabs } from '@melt-ui/svelte';
    import dayjs from 'dayjs';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import { pushToDataLayer } from '$saga/analytics/core/dispatcher';
    import Button from '$saga/components/Button.svelte';
    import CalendarPicker from '$saga/components/CalendarPicker.svelte';
    import { locale as localeStore } from '$saga/store/locale';
    import { filtersContext } from '$saga/views/Search/store';
    import { debouncedHandleFilterChange } from '$saga/views/Search/utils';

    import FlexibilityFilter from './FlexibilityFilter.svelte';

    export let handleValidateSelection: () => void;
    export let overflowContainer: HTMLElement | null = null;

    const NUMBER_OF_MONTHS = 3;

    // Track month selection for analytics
    function trackMonthSelection(
        monthLabel: string,
        action: 'select' | 'deselect',
    ): void {
        pushToDataLayer({
            event: 'search_month_click',
            eventData: {
                label: monthLabel,
                action: action,
            },
        });
    }

    const {
        elements: {
            calendar,
            cell,
            grid,
            heading,
            nextButton,
            prevButton,
            content,
        },
        states: { value, months, headingValue, weekdays },
        helpers: { isDateDisabled, isDateUnavailable },
    } = createDateRangePicker({
        locale: $localeStore.split('-')[0],
        minValue: parseDate(dayjs().add(1, 'day').format('YYYY-MM-DD')),
        // if endDateValue is present, then we chose a month
        defaultValue: {
            start: $filtersContext.formValues.startDate,
            end: $filtersContext.formValues.endDate,
        },
        forceVisible: true,
        numberOfMonths: NUMBER_OF_MONTHS,
        onValueChange: (dateValue) => {
            // Clear months when switching to specific date selection (date-selection)
            // This ensures month selection is cleared when user picks a specific date
            if ($valueTabs === 'date-selection') {
                $filtersContext.formValues.months = [];
                $filtersContext.formLabels.months = [];
                $filtersContext.formValues.endDate = undefined;
                $filtersContext.formValues.startDate = undefined;
                $filtersContext.formLabels.endDate = undefined;
                $filtersContext.formLabels.startDate = undefined;
            }

            if (dateValue.next.start && dateValue.next.end) {
                $filtersContext.formValues.startDate = dateValue.next.start;
                $filtersContext.formValues.endDate = dateValue.next.end;

                $filtersContext.formLabels.startDate = dateValue.next.start;
                $filtersContext.formLabels.endDate = dateValue.next.end;

                debouncedHandleFilterChange();
            }

            return dateValue.next;
        },
    });

    const {
        elements: { root, list, content: contentTabs, trigger },
        states: { value: valueTabs },
    } = createTabs({
        defaultValue: 'month-selection',
    });
    const triggers = [
        { id: 'month-selection', title: trans('tour.dates.departure_month') },
        { id: 'date-selection', title: trans('common.dates') },
    ];

    //
    $: {
        if ($filtersContext.formValues.months.length > 0) {
            $valueTabs = 'month-selection';
        } else {
            $valueTabs = 'date-selection';
        }
    }
</script>

<div
    {...$root}
    use:root
    class={cn(
        'sticky inset-x-0 top-0 z-50 flex w-full justify-center bg-white px-l py-s pt-s desktop:px-3xl',
    )}
>
    <div
        {...$list}
        use:list
        class="flex gap-m rounded-full bg-container-quiet p-xs"
    >
        {#each triggers as triggerItem (triggerItem.id)}
            <button
                {...$trigger}
                use:trigger
                class={cn(
                    'full rounded-full px-xs py-2xs text-xs text-content-brand transition-colors duration-300',
                    $valueTabs === triggerItem.id &&
                        'bg-container-brand text-white',
                )}
                on:click={() => ($valueTabs = triggerItem.id)}
            >
                {triggerItem.title}
            </button>
        {/each}
    </div>
</div>

<div {...$contentTabs('month-selection')} use:contentTabs class="flex-1">
    <div class="mx-auto mt-xl grid w-fit grid-cols-3 gap-2xl pb-xl">
        <!--    display the 12 next months as a button -->
        {#each Array(12) as _, index (index)}
            {@const month = dayjs().add(index, 'month')}
            {@const monthId = month.format('YYYY-MM')}
            {@const isSelected =
                $filtersContext.formValues.months.includes(monthId)}
            <button
                class={cn(
                    'flex size-[80px] flex-col items-center justify-center gap-xs rounded-xl border border-solid border-border-neutral text-sm',
                    'hover:border-border-brand hover:bg-container-commercial-neutral hover:text-content-brand',
                    isSelected &&
                        'border-border-brand bg-container-brand text-white',
                )}
                on:click={() => {
                    const monthLabel = month.format('MMM YYYY');

                    // Clear startDate and endDate when selecting months
                    $filtersContext.formValues.startDate = undefined;
                    $filtersContext.formValues.endDate = undefined;
                    $filtersContext.formLabels.startDate = undefined;
                    $filtersContext.formLabels.endDate = undefined;
                    $filtersContext.formValues.flexibility = 0;

                    // Toggle month selection
                    if (isSelected) {
                        // Remove month from selection
                        $filtersContext.formValues.months =
                            $filtersContext.formValues.months.filter(
                                (m) => m !== monthId,
                            );
                        $filtersContext.formLabels.months =
                            $filtersContext.formLabels.months.filter(
                                (m) => m !== monthLabel,
                            );
                        trackMonthSelection(monthLabel, 'deselect');
                    } else {
                        // Add month to selection
                        $filtersContext.formValues.months = [
                            ...$filtersContext.formValues.months,
                            monthId,
                        ];
                        $filtersContext.formLabels.months = [
                            ...$filtersContext.formLabels.months,
                            monthLabel,
                        ];
                        trackMonthSelection(monthLabel, 'select');
                    }

                    // Clear the calendar picker value
                    $value = {
                        start: undefined,
                        end: undefined,
                    };
                    debouncedHandleFilterChange();
                }}
            >
                <VpCalendarIcon class="size-xl" />
                {month.format('MMM YY')}
            </button>
        {/each}
    </div>
</div>

<div {...$contentTabs('date-selection')} use:contentTabs>
    <div {...content} use:content>
        <CalendarPicker
            {calendar}
            {prevButton}
            {heading}
            {headingValue}
            {nextButton}
            {months}
            {grid}
            {weekdays}
            {isDateDisabled}
            {isDateUnavailable}
            {cell}
            monthsOrientation="vertical"
            numberOfMonths={NUMBER_OF_MONTHS}
            {overflowContainer}
            displayNextMonthsLabel={trans(
                'tour.dates.display_next_months_label',
            )}
            displayPreviousMonthsLabel={trans(
                'tour.dates.display_previous_months_label',
            )}
        />
    </div>
</div>

<div class="sticky bottom-0 left-0 w-full bg-white py-s">
    {#if $valueTabs === 'date-selection'}
        <FlexibilityFilter />
    {/if}

    <div
        class="flex w-full justify-between border-t border-solid border-border-quiet py-s"
    >
        <Button
            size="small"
            intent="secondary"
            shape="rounded"
            type="button"
            on:click={() => {
                $filtersContext.formValues.startDate = undefined;
                $filtersContext.formValues.endDate = undefined;
                $filtersContext.formValues.months = [];
                $filtersContext.formValues.flexibility = 0;
                $filtersContext.formLabels.startDate = undefined;
                $filtersContext.formLabels.endDate = undefined;
                $filtersContext.formLabels.months = [];
                $value = { start: undefined, end: undefined };

                debouncedHandleFilterChange();
                handleValidateSelection();
            }}
            startIcon={VpReturnIcon}
            >{trans('search.filter.sidebar.clear')}
        </Button>
        <Button
            size="small"
            shape="rounded"
            type="button"
            startIcon={VpCheckIcon}
            on:click={() => {
                $filtersContext.formValues.startDate = $value.start;
                $filtersContext.formValues.endDate = $value.end;

                $filtersContext.formLabels.startDate = $value.start;
                $filtersContext.formLabels.endDate = $value.end;

                debouncedHandleFilterChange();

                handleValidateSelection();
            }}
        >
            {trans('search.filter.sidebar.apply')}
        </Button>
    </div>
</div>

<!-- Override default CalendarPicker style to adapt to a DatePickerRange interaction -->
<style lang="postcss">
    :global([data-melt-calendar-cell][data-selected][data-selection-start]) {
        @apply bg-blue-brand text-white !important;
    }

    :global([data-melt-calendar-cell][data-selected][data-selection-end]) {
        @apply bg-blue-brand text-white !important;
    }

    :global([data-melt-calendar-cell][data-selected]) {
        @apply bg-container-commercial-neutral text-content-neutral !important;
    }

    :global(
        td:has(
            [data-melt-calendar-cell][data-selected]:not(
                    [data-selection-end]
                ):not([data-outside-month])
        )
    ) {
        @apply bg-container-commercial-neutral !important;
    }

    :global(
        td:has(
            [data-melt-calendar-cell][data-highlighted]:not(
                    [data-selection-end]
                ):not([data-outside-month])
        )
    ) {
        @apply bg-container-commercial-neutral !important;
    }

    :global(table) {
        @apply border-collapse !important;
    }
</style>
